import { z } from 'zod';
import { attestationSchema, formResponseSchema } from './issuance.js';
/**
 * Transfers between soldiers (REQUIREMENTS §2.5, §3.2). Decided 2026-09-12
 * as "no third-party approver"; **reversed by the owner on 2026-09-21**: a
 * transfer completes only when someone *above everyone involved* signs it
 * off. The order is: initiator → the receiving soldier signs the form in
 * their own session → an approver above the giver, the receiver *and* the
 * initiator approves → custody moves. The item stays on the original
 * holder's account until the approval lands. Admin and csm may initiate on
 * anyone's behalf — that initiation is not the sign-off; someone else,
 * above them, still approves.
 *
 * "Above" is `ROLE_LEVEL` in shared: the required level is one more than
 * the highest level among the three parties, capped at the top level, and
 * the approver must also reach both soldiers (`canIssueTo`) and be none
 * of the three. `approvalLevel` is frozen on the transfer at creation from
 * the parties' Auth claims — never from the `users.roles` mirror.
 *
 * The owner also asked that admin/csm be able to complete a
 * transfer *without* the receiver's signature. That collides with the
 * non-negotiable "a commander can never sign on behalf of a soldier", so it
 * is built as an **attestation**: the assignment goes active with
 * `signatureId: null`, an `attestation` written under the admin's *own*
 * drawn signature and a comment, a `transfer_override` ledger entry, and a
 * visible "not signed by the receiver" label. Nothing ever records a
 * signature the soldier did not make.
 */
export const transferStatusSchema = z.enum([
    /** The receiver's form is pending their signature. */
    'pending_receiver',
    /** The receiver signed; waiting for the sign-off of someone above everyone involved (owner, 2026-09-21). */
    'pending_approval',
    /** The receiver signed and an approver signed off; custody moved. */
    'completed',
    /** Admin/csm attested; custody moved without the receiver's signature. */
    'overridden',
    /** The receiver rejected the form. */
    'rejected',
    /** The initiator, the holder, or the storeroom withdrew it. */
    'cancelled',
    /** An approver refused it after the receiver had signed; the holder keeps the item. */
    'declined',
]);
/** Who signed a transfer off, and when. No drawn image: the ledger entry is the record. */
export const transferApprovalSchema = z.object({
    byUid: z.string().min(1),
    byName: z.string(),
    comment: z.string().nullable(),
    at: z.string().nullable(),
});
export const transferSchema = z.object({
    id: z.string().min(1),
    /** The holder's assignment; stays `active` until the transfer completes. */
    assignmentId: z.string().min(1),
    fromUid: z.string().min(1),
    toUid: z.string().min(1),
    fromName: z.string(),
    toName: z.string(),
    catalogItemId: z.string().min(1),
    inventoryUnitId: z.string().nullable(),
    quantity: z.number().int().min(1),
    nameHe: z.string(),
    nameEn: z.string(),
    serialNumber: z.string().nullable(),
    status: transferStatusSchema,
    /** The receiver's form, pending their signature. */
    formId: z.string().min(1),
    /** The receiver's assignment once custody moved. */
    newAssignmentId: z.string().nullable(),
    initiatedBy: z.string().min(1),
    reason: z.string().nullable(),
    createdAt: z.string().nullable(),
    closedAt: z.string().nullable(),
    /** Why it was cancelled or declined, if it was. */
    closeReason: z.string().nullable(),
    attestation: attestationSchema.nullable(),
    /** The `ROLE_LEVEL` an approver must hold: one above the highest party, capped at the top. */
    approvalLevel: z.number().int().min(0),
    approval: transferApprovalSchema.nullable(),
    /**
     * Whether *this caller* may approve or decline it right now — decided by
     * the server (level, reach, not a party), because roles alone cannot say.
     */
    canApprove: z.boolean(),
});
/**
 * POST /api/transfers — by the holder, a commander in their chain, or the
 * storeroom. The receiver is picked from `GET /transfers/recipients`.
 */
export const createTransferRequestSchema = z.object({
    assignmentId: z.string().min(1),
    toUid: z.string().min(1).max(128),
    reason: z.string().trim().max(500).nullable().default(null),
});
/**
 * GET /api/transfers/recipients?q= — who can receive a transfer (owner,
 * 2026-09-21: "search by name and personal number"). Any signed-in user may
 * search the whole company by name or personal number; the answer is the
 * name and placement only. The personal number itself comes back only for
 * a person the caller may read in full (§3.7 stands: a soldier sees nobody
 * else's file). At least two characters, at most `TRANSFER_RECIPIENTS_MAX`
 * rows, exact matches on the personal number first.
 */
export const TRANSFER_RECIPIENTS_MAX = 10;
export const transferRecipientsQuerySchema = z.object({
    q: z.string().trim().min(2).max(60),
});
export const transferRecipientSchema = z.object({
    uid: z.string().min(1),
    firstName: z.string(),
    lastName: z.string(),
    rank: z.string().nullable(),
    /** The team, or the platoon for someone directly on it. */
    placementName: z.string().nullable(),
    /** Only when the caller may read this person's file; otherwise null. */
    personalNumber: z.string().nullable(),
});
export const transferRecipientsResponseSchema = z.object({ recipients: z.array(transferRecipientSchema) });
export const cancelTransferRequestSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
/** POST /api/transfers/:id/approve — the sign-off of someone above everyone involved. */
export const approveTransferRequestSchema = z.object({
    comment: z.string().trim().max(500).nullable().default(null),
});
/** POST /api/transfers/:id/decline — the same person says no, with a reason. */
export const declineTransferRequestSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
/**
 * POST /api/transfers/:id/override — admin or csm. The image is the *admin's* signature and is stored as an
 * attestation, never as the receiver's signature.
 */
export const overrideTransferRequestSchema = z.object({
    comment: z.string().trim().min(3).max(1000),
    imageDataUrl: z.string().regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/),
});
export const transferResponseSchema = z.object({ transfer: transferSchema });
export const createTransferResponseSchema = transferResponseSchema.merge(formResponseSchema);
/** GET /api/transfers — scoped: storeroom all; commanders their reach; a soldier their own, either side. */
export const transfersQuerySchema = z.object({
    status: transferStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const transfersResponseSchema = z.object({ transfers: z.array(transferSchema) });
//# sourceMappingURL=transfers.js.map